import { useRouter } from 'next/router';
import { Disclosure, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { matchSorter } from 'match-sorter';
import useTranslation from 'next-translate/useTranslation';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';
import useLogout from '@/utils/hooks/user/useLogout';

// Lib
import { ROLES } from '@/lib/user/roles';
import settingsMenu from '@/lib/menu/settings';

// Components
import { DefaultAvatar as Avatar } from '@/components/base/Avatar';
import Icon from '@/components/base/Icon';
import LinkBase from '@/components/base/LinkBase';
import ActionTag from '@/components/base/Tag/ActionTag';

const MenuSidebarSettings = () => {
    const { user } = useUser();
    const { pathname } = useRouter();
    const { t } = useTranslation();
    const { logout } = useLogout();

    const urlWithoutParams = pathname.split('?')[0];
    const isActive =
        matchSorter(settingsMenu, urlWithoutParams, {
            keys: [
                { threshold: matchSorter.rankings.EQUAL, key: 'link' },
                { threshold: matchSorter.rankings.EQUAL, key: 'urlActiveMenu' }
            ]
        }).length > 0;

    return (
        <Disclosure>
            {({ open }) => (
                <>
                    <Disclosure.Button
                        className="flex w-full items-center justify-between space-x-3 focus:ring-offset-0"
                        data-testid="menu-settings-mobile-btn">
                        <div className="flex w-[calc(100%-24px)] space-x-3 text-left">
                            <Avatar
                                fallback={
                                    user?.first_name && user?.last_name
                                        ? user.first_name.charAt(0) + user.last_name.charAt(0)
                                        : ''
                                }
                                radius="rounded-md"
                                url={user?.settings?.avatar ?? ''}
                                selected={isActive}
                            />
                            <div className="flex flex-1 flex-col justify-between space-y-1 overflow-hidden">
                                <p className="truncate text-sm">
                                    {`${user?.first_name ?? ''} ${user?.last_name ?? ''}`}
                                </p>
                                <div className="w-fit">
                                    <ActionTag variant="role" role={user.acl_role}>
                                        <span className="text-xxs">
                                            {t(ROLES[user?.acl_role]?.label)}
                                        </span>
                                    </ActionTag>
                                </div>
                            </div>
                        </div>
                        <Icon
                            icon={open ? 'chevron-top' : 'chevron-bottom'}
                            className="text-xs text-icoGray-500"
                        />
                    </Disclosure.Button>

                    <Transition
                        enter="transition duration-150 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0">
                        <Disclosure.Panel static className="mt-5 space-y-3.5 pl-3.5">
                            {settingsMenu.map((subMenu, key) => {
                                if (subMenu.name === 'separator_content') {
                                    return;
                                }

                                if (subMenu.acl) {
                                    if (subMenu.acl.roles) {
                                        if (!subMenu.acl.roles.includes(user.acl_role)) {
                                            return null;
                                        }
                                    }
                                }

                                const isSubMenuActive =
                                    subMenu.link === urlWithoutParams ||
                                    (subMenu?.urlActiveMenu &&
                                        subMenu.urlActiveMenu.includes(urlWithoutParams));
                                return (
                                    <LinkBase
                                        href={subMenu.link}
                                        target={subMenu.target_blank ? '_blank' : ''}
                                        rel={subMenu.target_blank ? 'noopener noreferrer' : null}
                                        className={clsx(
                                            'flex w-full items-center space-x-3 text-sm hover:text-icoBlue',
                                            {
                                                'text-icoBlue': isSubMenuActive
                                            }
                                        )}
                                        key={key}>
                                        <Icon icon={subMenu.icon} className="text-icoBlue" />
                                        <span className="text-xs">{t(subMenu.name)}</span>
                                    </LinkBase>
                                );
                            })}
                            <button
                                className="flex w-full items-center space-x-3 text-sm hover:text-icoBlue"
                                onClick={logout}>
                                <Icon icon={'logout'} className="text-base text-icoBlue" />
                                <span className="text-xs capitalize">{t('commons:logout')}</span>
                            </button>
                        </Disclosure.Panel>
                    </Transition>
                </>
            )}
        </Disclosure>
    );
};

export default MenuSidebarSettings;
